import React from 'react';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import type { AggregationArgs } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import type {
    FiltersConfig,
    ModalFiltersConfigSection,
} from '@rocco/ui/filter-sort/types/filter-config-section';
import { FiltersModalElement } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';
import { isSectionVisible } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalSection';
import {
    getRefineSectionPresentation,
    getSectionSummary,
    resolveSectionFilterState,
} from '@rocco/ui/filter-sort/components/filters-modal/refine/get-refine-presentation';
import { RefineDrillInRow } from '@rocco/ui/filter-sort/components/filters-modal/refine/RefineDrillInRow';
import { RefineExpander } from '@rocco/ui/filter-sort/components/filters-modal/refine/RefineExpander';
import { RefinePillGroup } from '@rocco/ui/filter-sort/components/filters-modal/refine/RefinePillGroup';
import {
    MORE_FILTERS_TITLE,
    type RefineSubView,
} from '@rocco/ui/filter-sort/components/filters-modal/refine/refine-view';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type {
    FilterStates,
    FilterStatesKey,
} from '@rocco/ui/filter-sort/view-models/filter-states';

type UpdateFilterState = (
    filterKey: FilterStatesKey,
    newState: FilterState,
    shouldAggregate?: boolean,
) => void;

interface RefineSectionListProps {
    config: FiltersConfig;
    facetSections: ModalFiltersConfigSection[];
    filterStates: FilterStates;
    onUpdateFilterState: UpdateFilterState;
    onUpdateFacetFilterState: UpdateFilterState;
    onAlphabeticalGrouping: (args: AggregationArgs) => void;
    onDrillIn: (view: RefineSubView) => void;
}

const SectionTitle = ({ children }: React.PropsWithChildren) => (
    <div className="flex min-h-11 items-center text-sm font-medium">
        {children}
    </div>
);

/**
 * The refine sheet's root view: one uniform list of drill-in rows carrying
 * staged summaries, with location inline and admin-only sections collected
 * behind a single "More filters" row. Pills and expanders remain available
 * through per-section overrides.
 */
export const RefineSectionList = (props: RefineSectionListProps) => {
    const {
        config,
        facetSections,
        filterStates,
        onUpdateFilterState,
        onUpdateFacetFilterState,
        onAlphabeticalGrouping,
        onDrillIn,
    } = props;

    const visibleSections = [
        ...config.modalFilters.map((section) => ({ section, isFacet: false })),
        ...facetSections.map((section) => ({ section, isFacet: true })),
    ].filter(({ section }) => isSectionVisible(section, filterStates));

    const mainSections = visibleSections.filter(
        ({ section }) =>
            getRefineSectionPresentation(section, filterStates) !==
            'moreFilters',
    );
    const moreFilterSections = visibleSections.filter(
        ({ section }) =>
            getRefineSectionPresentation(section, filterStates) ===
            'moreFilters',
    );

    const renderElements = (
        section: ModalFiltersConfigSection,
        isFacet: boolean,
        filters = section.filters,
    ) =>
        filters.map((filterConfigItem) => (
            <FiltersModalElement
                key={filterConfigItem.filterKey}
                displayMode={DisplayMode.Mobile}
                filterConfigItem={filterConfigItem}
                filterState={resolveSectionFilterState(
                    filterConfigItem,
                    filterStates,
                )}
                onUpdateFilterState={
                    isFacet ? onUpdateFacetFilterState : onUpdateFilterState
                }
                onAlphabeticalGrouping={onAlphabeticalGrouping}
                rangeVariant="sidebar"
            />
        ));

    return (
        <div className="flex flex-col">
            {mainSections.map(({ section, isFacet }) => {
                const presentation = getRefineSectionPresentation(
                    section,
                    filterStates,
                );
                const update = isFacet
                    ? onUpdateFacetFilterState
                    : onUpdateFilterState;
                const key = `refine-${section.title}`;

                if (presentation === 'pills') {
                    return (
                        <section key={key} aria-label={section.title}>
                            <SectionTitle>{section.title}</SectionTitle>
                            <RefinePillGroup
                                filters={section.filters}
                                filterStates={filterStates}
                                onUpdateFilterState={update}
                            />
                        </section>
                    );
                }

                if (presentation === 'inline') {
                    return (
                        <section key={key} aria-label={section.title}>
                            <SectionTitle>{section.title}</SectionTitle>
                            <div className="pb-3">
                                {renderElements(section, isFacet)}
                            </div>
                        </section>
                    );
                }

                if (presentation === 'expander') {
                    const enumFilters = section.filters.filter(
                        (filter) => filter.type === 'default',
                    );
                    const rangeFilters = section.filters.filter(
                        (filter) =>
                            filter.type === 'price' ||
                            filter.type === 'simpleRange',
                    );

                    return (
                        <RefineExpander
                            key={key}
                            title={section.title}
                            summary={getSectionSummary(section, filterStates)}
                        >
                            {enumFilters.length > 0 && (
                                <RefinePillGroup
                                    filters={enumFilters}
                                    filterStates={filterStates}
                                    onUpdateFilterState={update}
                                />
                            )}
                            {renderElements(section, isFacet, rangeFilters)}
                        </RefineExpander>
                    );
                }

                return (
                    <RefineDrillInRow
                        key={key}
                        title={section.title}
                        summary={getSectionSummary(section, filterStates)}
                        onClick={() =>
                            onDrillIn({
                                type: 'section',
                                sectionTitle: section.title,
                                isFacet,
                            })
                        }
                    />
                );
            })}

            {moreFilterSections.length > 0 && (
                <RefineDrillInRow
                    title={MORE_FILTERS_TITLE}
                    summary={moreFilterSections
                        .map(({ section }) =>
                            getSectionSummary(section, filterStates),
                        )
                        .filter(Boolean)
                        .join(' · ')}
                    onClick={() => onDrillIn({ type: 'moreFilters' })}
                />
            )}
        </div>
    );
};
